/**
 * UeberweisungenPage — Überweisungen & Freigabe-Workflow
 *
 * Features:
 * - KPI-Karten (Entwürfe, Ausstehend, Ausgeführt)
 * - Visueller Workflow: Entwurf -> Freigabe angefordert -> Freigegeben -> Ausgeführt
 * - Tabelle mit Aktionen pro Status
 * - "Neue Überweisung" Dialog
 * - finAPI Info-Banner (geplant)
 */

import { useState, useMemo } from 'react';
import { useUeberweisungen, createUeberweisung, updateUeberweisung, useEingangsbelege } from '@/hooks/useBuchhaltung';
import { useBetriebe } from '@/hooks/useSupabase';
import type { Ueberweisung, UeberweisungStatus, FirmaId } from '@/lib/database.types';
import {
  CreditCard, Send, CheckCircle2, Clock, AlertCircle,
  Plus, X, Loader2, Building2, ArrowRight, Shield,
  Banknote, FileText, ChevronRight, Zap, Eye, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================
// HELPERS
// ============================================================

function formatEuro(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const STATUS_CONFIG: Record<string, { label: string; color: string; step: number }> = {
  entwurf: { label: 'Entwurf', color: 'bg-gray-100 text-gray-600', step: 0 },
  freigabe_angefordert: { label: 'Freigabe angefordert', color: 'bg-amber-50 text-amber-600', step: 1 },
  freigegeben: { label: 'Freigegeben', color: 'bg-blue-50 text-blue-600', step: 2 },
  ausgefuehrt: { label: 'Ausgeführt', color: 'bg-emerald-50 text-emerald-600', step: 3 },
  fehlgeschlagen: { label: 'Fehlgeschlagen', color: 'bg-red-50 text-red-600', step: -1 },
  storniert: { label: 'Storniert', color: 'bg-gray-50 text-gray-400', step: -1 },
};

const FIRMA_LABELS: Record<string, string> = {
  gutachter_koop: 'Gutachter Koop',
  lcs_gmbh: 'LCS GmbH',
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// ============================================================
// KPI CARD
// ============================================================

function KPICard({ label, value, subtext, icon: Icon, color }: {
  label: string; value: string; subtext?: string; icon: typeof CreditCard; color: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 tracking-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {subtext && <div className="text-[10px] text-gray-400 mt-1">{subtext}</div>}
    </div>
  );
}

// ============================================================
// WORKFLOW VISUAL
// ============================================================

function WorkflowSteps() {
  const steps = [
    { label: 'Entwurf', icon: FileText, color: '#6B7280' },
    { label: 'Freigabe angefordert', icon: Clock, color: '#D97706' },
    { label: 'Freigegeben', icon: CheckCircle2, color: '#2563EB' },
    { label: 'Ausgeführt', icon: Send, color: '#059669' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between gap-2">
        {steps.map((step, i) => (
          <div key={step.label} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${step.color}12` }}>
                <step.icon className="w-4 h-4" style={{ color: step.color }} />
              </div>
              <span className="text-xs font-medium text-gray-600 hidden sm:inline">{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0 mx-1" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// NEUE ÜBERWEISUNG DIALOG
// ============================================================

function NeueUeberweisungDialog({ open, onClose, onSave, firma }: {
  open: boolean;
  onClose: () => void;
  onSave: (u: Partial<Ueberweisung>) => void;
  firma: FirmaId;
}) {
  const { data: betriebe } = useBetriebe();
  const { data: belegeRaw } = useEingangsbelege(firma, 'freigegeben');
  const belege = belegeRaw || [];

  const [betriebId, setBetriebId] = useState('');
  const [empfaengerName, setEmpfaengerName] = useState('');
  const [empfaengerIban, setEmpfaengerIban] = useState('');
  const [empfaengerBic, setEmpfaengerBic] = useState('');
  const [betrag, setBetrag] = useState('');
  const [verwendungszweck, setVerwendungszweck] = useState('');
  const [ausfuehrungsdatum, setAusfuehrungsdatum] = useState('');
  const [fromBelegId, setFromBelegId] = useState('');
  const [saving, setSaving] = useState(false);

  // Prefill from beleg
  const handleBelegSelect = (belegId: string) => {
    setFromBelegId(belegId);
    const beleg = belege.find(b => b.id === belegId);
    if (beleg) {
      setEmpfaengerName(beleg.absender_firma || beleg.absender_name || '');
      setEmpfaengerIban(beleg.absender_iban || '');
      setEmpfaengerBic(beleg.absender_bic || '');
      setBetrag(String(beleg.brutto_betrag || 0));
      setVerwendungszweck(beleg.verwendungszweck || beleg.beleg_nr || '');
    }
  };

  if (!open) return null;

  const selectedBetrieb = (betriebe || []).find(b => b.id === betriebId);

  const handleSave = async () => {
    if (!empfaengerName.trim()) { toast.error('Empfänger-Name erforderlich'); return; }
    if (!empfaengerIban.trim()) { toast.error('Empfänger-IBAN erforderlich'); return; }
    if (!betrag || parseFloat(betrag) <= 0) { toast.error('Betrag erforderlich'); return; }
    setSaving(true);
    try {
      onSave({
        firma,
        betrieb_id: betriebId || null,
        auftraggeber_iban: selectedBetrieb?.iban || null,
        auftraggeber_bic: selectedBetrieb?.bic || null,
        auftraggeber_name: selectedBetrieb?.firma || null,
        empfaenger_name: empfaengerName,
        empfaenger_iban: empfaengerIban,
        empfaenger_bic: empfaengerBic || null,
        betrag: parseFloat(betrag),
        verwendungszweck: verwendungszweck || null,
        ausfuehrungsdatum: ausfuehrungsdatum || null,
        eingangsbeleg_id: fromBelegId || null,
        status: 'entwurf' as UeberweisungStatus,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Neue Überweisung</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* From Beleg */}
          {belege.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Aus Eingangsbeleg (optional)</label>
              <select value={fromBelegId} onChange={e => handleBelegSelect(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="">— Manuell eingeben —</option>
                {belege.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.absender_firma || b.absender_name || '—'} — {formatEuro(b.brutto_betrag)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Betrieb (Auftraggeber-Konto) */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Auftraggeber-Konto</label>
            <select value={betriebId} onChange={e => setBetriebId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
              <option value="">— Konto wählen —</option>
              {(betriebe || []).filter(b => b.aktiv).map(b => (
                <option key={b.id} value={b.id}>{b.firma} ({b.iban || 'keine IBAN'})</option>
              ))}
            </select>
          </div>
          {/* Empfänger */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Empfänger Name</label>
            <input value={empfaengerName} onChange={e => setEmpfaengerName(e.target.value)}
              placeholder="Empfänger"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">IBAN</label>
              <input value={empfaengerIban} onChange={e => setEmpfaengerIban(e.target.value)}
                placeholder="DE89..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">BIC</label>
              <input value={empfaengerBic} onChange={e => setEmpfaengerBic(e.target.value)}
                placeholder="WELADED1ISL"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
          </div>
          {/* Betrag & Datum */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Betrag</label>
              <input type="number" step="0.01" value={betrag} onChange={e => setBetrag(e.target.value)}
                placeholder="0,00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Ausführungsdatum</label>
              <input type="date" value={ausfuehrungsdatum} onChange={e => setAusfuehrungsdatum(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
          </div>
          {/* Verwendungszweck */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Verwendungszweck</label>
            <input value={verwendungszweck} onChange={e => setVerwendungszweck(e.target.value)}
              placeholder="z.B. Rechnung Nr. 12345"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Erstellen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function UeberweisungenPage() {
  const [firma, setFirma] = useState<FirmaId>('gutachter_koop');
  const [statusFilter, setStatusFilter] = useState<UeberweisungStatus | ''>('');
  const [showNeu, setShowNeu] = useState(false);

  const { data: ueberweisungenRaw, loading, refetch } = useUeberweisungen(
    firma,
    statusFilter || undefined,
  );
  const ueberweisungen = ueberweisungenRaw || [];

  // KPIs
  const kpis = useMemo(() => {
    const entwuerfe = ueberweisungen.filter(u => u.status === 'entwurf');
    const ausstehend = ueberweisungen.filter(u => ['freigabe_angefordert', 'freigegeben'].includes(u.status));
    const ausgefuehrt = ueberweisungen.filter(u => u.status === 'ausgefuehrt');
    return {
      entwuerfe: entwuerfe.length,
      entwuerfeBetrag: entwuerfe.reduce((s, u) => s + u.betrag, 0),
      ausstehend: ausstehend.length,
      ausstehendBetrag: ausstehend.reduce((s, u) => s + u.betrag, 0),
      ausgefuehrt: ausgefuehrt.length,
      ausgefuehrtBetrag: ausgefuehrt.reduce((s, u) => s + u.betrag, 0),
    };
  }, [ueberweisungen]);

  const handleCreate = async (u: Partial<Ueberweisung>) => {
    try {
      await createUeberweisung(u);
      toast.success('Überweisung erstellt');
      setShowNeu(false);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    }
  };

  const handleStatusChange = async (id: string, newStatus: UeberweisungStatus) => {
    try {
      const updates: Partial<Ueberweisung> = { status: newStatus };
      if (newStatus === 'freigabe_angefordert') {
        updates.freigabe_angefordert_am = new Date().toISOString();
      }
      if (newStatus === 'freigegeben') {
        updates.freigegeben_am = new Date().toISOString();
      }
      await updateUeberweisung(id, updates);
      toast.success(`Status: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Überweisungen</h1>
          <p className="text-sm text-gray-500 mt-1">Zahlungsaufträge erstellen, freigeben und ausführen</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={firma} onChange={e => setFirma(e.target.value as FirmaId)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20">
            <option value="gutachter_koop">Gutachter Koop</option>
            <option value="lcs_gmbh">LCS GmbH</option>
          </select>
          <button onClick={() => setShowNeu(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors min-h-[44px]">
            <Plus className="w-4 h-4" />
            Neue Überweisung
          </button>
        </div>
      </div>

      {/* finAPI Banner */}
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Zap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-medium text-purple-900">finAPI-Integration (geplant)</div>
          <div className="text-xs text-purple-700 mt-0.5">
            Zukünftig werden Überweisungen direkt über finAPI an die Bank übermittelt.
            Aktuell dient diese Seite zur Vorbereitung und Freigabe von Zahlungsaufträgen.
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KPICard
          label="Entwürfe"
          value={String(kpis.entwuerfe)}
          subtext={formatEuro(kpis.entwuerfeBetrag)}
          icon={FileText}
          color="#6B7280"
        />
        <KPICard
          label="Ausstehend"
          value={String(kpis.ausstehend)}
          subtext={formatEuro(kpis.ausstehendBetrag)}
          icon={Clock}
          color="#D97706"
        />
        <KPICard
          label="Ausgeführt"
          value={String(kpis.ausgefuehrt)}
          subtext={formatEuro(kpis.ausgefuehrtBetrag)}
          icon={CheckCircle2}
          color="#059669"
        />
      </div>

      {/* Workflow */}
      <WorkflowSteps />

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as UeberweisungStatus | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20">
          <option value="">Alle Status</option>
          <option value="entwurf">Entwurf</option>
          <option value="freigabe_angefordert">Freigabe angefordert</option>
          <option value="freigegeben">Freigegeben</option>
          <option value="ausgefuehrt">Ausgeführt</option>
          <option value="fehlgeschlagen">Fehlgeschlagen</option>
          <option value="storniert">Storniert</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {ueberweisungen.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="text-sm">Keine Überweisungen vorhanden</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Empfänger</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">IBAN</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Betrag</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Verwendungszweck</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Datum</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {ueberweisungen.map(u => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{u.empfaenger_name}</div>
                      {u.eingangsbelege && (
                        <div className="text-[10px] text-gray-400">Beleg: {u.eingangsbelege.beleg_nr || '—'}</div>
                      )}
                      {u.kommentar && (
                        <div className="text-[10px] text-gray-500 mt-1 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="whitespace-pre-wrap">{u.kommentar}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-600">{u.empfaenger_iban}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-gray-900">{formatEuro(u.betrag)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600 truncate block max-w-[200px]">{u.verwendungszweck || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{formatDate(u.ausfuehrungsdatum)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={async () => {
                          const k = window.prompt('Kommentar (Irina ↔ Andreas):', u.kommentar || '');
                          if (k === null) return;
                          try { await updateUeberweisung(u.id, { kommentar: k || null }); toast.success('Kommentar gespeichert'); refetch(); }
                          catch (err: any) { toast.error('Fehler: ' + (err.message || 'Unbekannt')); }
                        }} className="px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors" title="Kommentar">
                          <MessageSquare className="w-3 h-3" />
                        </button>
                        {u.status === 'entwurf' && (
                          <button onClick={() => handleStatusChange(u.id, 'freigabe_angefordert')}
                            className="px-2 py-1 text-[10px] font-medium text-amber-600 bg-amber-50 rounded hover:bg-amber-100 transition-colors">
                            Freigabe anfordern
                          </button>
                        )}
                        {u.status === 'freigabe_angefordert' && (
                          <button onClick={() => handleStatusChange(u.id, 'freigegeben')}
                            className="px-2 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                            Freigeben
                          </button>
                        )}
                        {u.status === 'freigegeben' && (
                          <button onClick={() => handleStatusChange(u.id, 'ausgefuehrt')}
                            className="px-2 py-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors">
                            Als ausgeführt markieren
                          </button>
                        )}
                        {!['ausgefuehrt', 'storniert'].includes(u.status) && (
                          <button onClick={() => handleStatusChange(u.id, 'storniert')}
                            className="px-2 py-1 text-[10px] font-medium text-red-500 bg-red-50 rounded hover:bg-red-100 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Neue Überweisung Dialog */}
      <NeueUeberweisungDialog
        open={showNeu}
        onClose={() => setShowNeu(false)}
        onSave={handleCreate}
        firma={firma}
      />
    </div>
  );
}
